import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accessibilityService } from '../services/api/accessibilityService'

export const useAccessibility = () => {
  const queryClient = useQueryClient()

  // Get scan by ID mutation
  const getScanByIdMutation = useMutation({
    mutationFn: (scanId) => accessibilityService.getScanById(scanId),
  })

  // Scan URL mutation
  const scanUrlMutation = useMutation({
    mutationFn: (scanData) => accessibilityService.scanUrl(scanData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessibility-history'] })
      queryClient.invalidateQueries({ queryKey: ['accessibility-latest'] })
    },
  })

  // Get scan history query
  const getScanHistoryQuery = (filters = {}) => {
    return queryClient.fetchQuery({
      queryKey: ['accessibility-history', filters],
      queryFn: () => accessibilityService.getScanHistory(filters),
    })
  }

  // Get latest scan query
  const getLatestScanQuery = (url) => {
    return queryClient.fetchQuery({
      queryKey: ['accessibility-latest', url],
      queryFn: () => accessibilityService.getLatestScan(url),
    })
  }

  // Get scans by website query
  const getScansByWebsiteQuery = (websiteConnectionId, pagination = {}) => {
    return queryClient.fetchQuery({
      queryKey: ['accessibility-website-scans', websiteConnectionId, pagination],
      queryFn: () => accessibilityService.getScansByWebsite(websiteConnectionId, pagination),
    })
  }

  // Get violations by website query
  const getViolationsByWebsiteQuery = (websiteConnectionId, filters = {}) => {
    return queryClient.fetchQuery({
      queryKey: ['accessibility-website-violations', websiteConnectionId, filters],
      queryFn: () => accessibilityService.getViolationsByWebsite(websiteConnectionId, filters),
    })
  }

  // Get website stats query
  const getWebsiteStatsQuery = (websiteConnectionId) => {
    return queryClient.fetchQuery({
      queryKey: ['accessibility-website-stats', websiteConnectionId],
      queryFn: () => accessibilityService.getWebsiteStats(websiteConnectionId),
    })
  }

  // Health check query
  const healthCheckQuery = () => {
    return queryClient.fetchQuery({
      queryKey: ['accessibility-health'],
      queryFn: () => accessibilityService.healthCheck(),
    })
  }

  return {
    // Get scan by ID
    getScanById: getScanByIdMutation.mutate,
    isGettingScanById: getScanByIdMutation.isPending,

    // Scan URL
    scanUrl: scanUrlMutation.mutate,
    isScanning: scanUrlMutation.isPending,
    scanError: scanUrlMutation.error,
    scanData: scanUrlMutation.data,

    // Get scan history
    getScanHistory: (filters) => getScanHistoryQuery(filters),

    // Get latest scan
    getLatestScan: (url) => getLatestScanQuery(url),

    // Get scans by website
    getScansByWebsite: (websiteConnectionId, pagination) => getScansByWebsiteQuery(websiteConnectionId, pagination),

    // Get violations by website
    getViolationsByWebsite: (websiteConnectionId, filters) => getViolationsByWebsiteQuery(websiteConnectionId, filters),

    // Get website stats
    getWebsiteStats: (websiteConnectionId) => getWebsiteStatsQuery(websiteConnectionId),

    // Health check
    healthCheck: () => healthCheckQuery(),
  }
}
